import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../api/axiosConfig";
import { useSession } from "../context/SessionContext";
import BackgroundPic from "./assets/sport.webp";
import Logo from "./assets/logo3.png";
import convertToEnglishDigits from "../utils/convertToEnglishDigits";

const mapRoleToSystem = (backendRole) => {
  const roleMap = {
    HeadCoach: "coach",
    Coach: "coach",
    Player: "player",
    Admin: "admin",
    Manager: "manager",
  };

  return roleMap[backendRole] || "unknown"; // Fallback to "unknown" if the role is not recognized
};

function LoginPage() {
  const { setSession } = useSession();
  const navigate = useNavigate();
  const theme = useTheme();

  const [step, setStep] = useState(1); // 1: phone input, 2: verification code
  const [nationalCode, setNationalCode] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // Loading state

  const handleSendCode = async () => {
    setError("");
    setLoading(true); // Start loading
    try {
      await axiosInstance.post(
        `/User/api/User/TwoFactorAuthenticate?username=${encodeURIComponent(
          nationalCode,
        )}`,
      );
      setStep(2); // Go to verification step
    } catch (err) {
      setError("ارسال کد تایید با مشکل مواجه شد.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleVerifyCode = async () => {
    setError("");
    setLoading(true); // Start loading
    try {
      // Pass query parameters using params field
      const response = await axiosInstance.post(
        "/User/api/User/VerifyCode",
        null,
        {
          params: {
            username: nationalCode,
            code: verificationCode,
          },
        },
      );

      // Extract the JWT token from the response
      const { token } = response.data;

      // Decode the JWT token
      const decodedToken = jwtDecode(token);
      // console.log(decodedToken, "role");
      // Map the backend role to the system role
      const backendRole =
        decodedToken[
          "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
        ];
      const systemRole = mapRoleToSystem(backendRole); // Convert the backend role to a system role

      // Save the session with the decoded data
      // console.log(
      //   {
      //     token,
      //     role: systemRole,
      //     user: {
      //       name: decodedToken[
      //         "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
      //       ],
      //       image: "",
      //     },
      //   },
      //   "sag",
      // );
      setSession({
        token,
        role: systemRole,
        user: {
          name: decodedToken[
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
          ],
          image: "",
          id: decodedToken[
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
          ],
        },
      });
      // Navigate to the dashboard or role-based page
      navigate("/dashboard");
    } catch (err) {
      setError("تایید کد با مشکل مواجه شد.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundImage: `url(${BackgroundPic})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        textAlign: "center",
        color: "#fff",
        padding: "20px",
      }}
    >
      {/* لایه نیمه‌شفاف */}
      <Box
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          borderRadius: "10px",
          padding: "30px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.7)",
          width: "90%",
          maxWidth: "400px",
          [theme.breakpoints.down("sm")]: {
            padding: "20px",
          },
        }}
      >
        {/* لوگو */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={Logo}
            alt="Logo"
            style={{
              width: "300px",
              height: "auto",
              marginBottom: "20px",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        </Box>

        {/* عنوان صفحه */}
        <Typography
          variant="h4"
          component="h2"
          mb={3}
          sx={{
            fontSize: "2rem",
            [theme.breakpoints.down("sm")]: {
              fontSize: "1.5rem",
            },
          }}
        >
          ورود به سامانه
        </Typography>

        {/* نمایش خطا */}
        {error && (
          <Typography
            variant="subtitle2"
            color="error"
            sx={{ marginBottom: "10px" }}
          >
            {error}
          </Typography>
        )}

        {/* فرم مرحله 1: وارد کردن شماره موبایل */}
        {step === 1 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              margin: "0 auto",
            }}
          >
            <TextField
              dir="rtl"
              required
              placeholder="کد ملی *"
              value={nationalCode}
              inputProps={{ pattern: "[0-9]*" }}
              onChange={(e) =>
                setNationalCode(convertToEnglishDigits(e.target.value))
              }
              fullWidth
              sx={{
                backgroundColor: "#fff",
                borderRadius: "5px",
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendCode}
              disabled={loading} // Disable button during loading
              sx={{
                marginTop: 2,
                padding: "10px 20px",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "ارسال کد تایید"
              )}
            </Button>
          </Box>
        )}

        {/* فرم مرحله 2: وارد کردن کد تایید */}
        {step === 2 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              margin: "0 auto",
            }}
          >
            <TextField
              dir="rtl"
              required
              placeholder="کد تایید را وارد کنید *"
              value={verificationCode}
              inputProps={{ pattern: "[0-9]*" }}
              onChange={(e) =>
                setVerificationCode(convertToEnglishDigits(e.target.value))
              }
              fullWidth
              sx={{
                backgroundColor: "#fff",
                borderRadius: "5px",
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleVerifyCode}
              disabled={loading} // Disable button during loading
              sx={{
                marginTop: 2,
                padding: "10px 20px",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "تایید کد"
              )}
            </Button>
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{
            marginTop: 3,
            cursor: "pointer",
            textDecoration: "underline",
            color: "#ffcc00",
          }}
          onClick={() => navigate("/sign-up")}
        >
          هنوز ثبت‌ نام نکرده‌اید؟ کلیک کنید.
        </Typography>
      </Box>
    </Box>
  );
}

export default LoginPage;
